/**
 * Phase 6b1 PR 7 — `useTeamMeets` hook.
 *
 * Read + write surface for the athlete's upcoming meets. Mirrors
 * `useRaceResults`'s shape (CRUD + held-delete-friendly state),
 * but the underlying data model is different (per design's locked
 * decision #4: race results vs upcoming meets are separate
 * contracts; no generic "event" abstraction).
 *
 * Scope (athlete view):
 *   The athlete needs to see BOTH:
 *     - Their personal solo meets (`team_meets.user_id = me`)
 *     - Their active-season team's meets (`team_meets.team_id = X`)
 *
 *   The vanilla `useUpcomingRace` hook (Phase 5 PR 5.1) does this
 *   via direct Supabase OR query. PR 7 routes through the API
 *   instead (per safety gates #6 + #8) — TWO parallel `GET
 *   /api/meets` fetches, merged client-side:
 *     1. `GET /api/meets`           → solo meets (server scopes by req.authUser.id)
 *     2. `GET /api/meets?teamId=X`  → team meets (server scopes by team_id)
 *
 *   The team_id comes from `useActiveSeason().data?.team_id`.
 *   When the athlete has no active season OR the season has no
 *   team (solo athlete), only the solo fetch fires.
 *
 * Filter: this hook returns ALL meets (past + upcoming). The
 * consumer (`<UpcomingMeetsList>`) filters to `meet_date >=
 * today` for display. The full dataset stays cached for derived
 * views (e.g., past-meets archive in a future phase).
 *
 * Writes (safety gate #6 + #8):
 *   - createMeet → POST /api/meets; server stamps user_id /
 *     team_id, validates team membership for team-scoped meets
 *     via #49.
 *   - updateMeet → PUT /api/meets/:id; ownership validated by
 *     authorizeMeetAccess.
 *   - deleteMeet → DELETE /api/meets/:id; same authorization.
 *     UI MUST route through `<DeleteConfirmDialog>` per safety
 *     gate #7 — the hook itself does not enforce confirmation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';
import { useActiveSeason } from './useActiveSeason';

export interface TeamMeet {
  id: string;
  team_id: string | null;
  user_id: string | null;
  meet_name: string;
  meet_date: string;            // 'YYYY-MM-DD'
  meet_time: string | null;     // 12-hour formatted, e.g., '9:00 AM'
  location: string | null;
  course_name: string | null;
  course_profile: string | null;
  distance: string[];           // multi-select array
  priority: 1 | 2 | 3;
  description: string | null;
  created_by: string;
  created_at?: string;
  updated_at?: string;
  /**
   * 2.0 wave 2 (spec 7.1 wire contract): the VIEWER's own goal time on this meet,
   * whole seconds, or null. Never another user's goal. Absent on rows from an API
   * that predates the field.
   */
  goal_time_seconds?: number | null;
}

/**
 * POST body shape for `createMeet`. Mirrors the backend's
 * `handleCreateMeet` (api/index.js:4920+). Server is the source
 * of truth for `user_id` and `created_by`; the client only
 * supplies `teamId` (optional, gates the meet as team-scoped or
 * solo).
 */
export interface CreateTeamMeetInput {
  teamId?: string | null;
  meetName: string;
  meetDate: string;             // 'YYYY-MM-DD'
  meetTime?: string | null;     // optional 12-hour or 24-hour string
  location?: string | null;
  courseName?: string | null;
  courseProfile?: string | null;
  distance?: string[];          // defaults to ['5K'] server-side
  priority?: 1 | 2 | 3;         // defaults to 3 (Regular) server-side
  description?: string | null;
  /**
   * 2.0 wave 2 (spec 7.1): optional goal time text, `m:ss` or `h:mm:ss`
   * (`lib/goalTime.ts`). On create, sent only when filled; on update, `null` or
   * `""` clears it and an absent key leaves the stored goal untouched.
   */
  goalTime?: string | null;
}

export type UpdateTeamMeetInput = Partial<CreateTeamMeetInput>;

export interface UseTeamMeetsResult {
  data: TeamMeet[] | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createMeet: (input: CreateTeamMeetInput) => Promise<TeamMeet>;
  updateMeet: (id: string, patch: UpdateTeamMeetInput) => Promise<TeamMeet>;
  /** Delete by row id. UI MUST wrap with `<DeleteConfirmDialog>`. */
  deleteMeet: (id: string) => Promise<void>;
}

interface ApiGetResponse {
  meets?: TeamMeet[];
  error?: string;
}

interface ApiCreateResponse {
  meet?: TeamMeet;
  error?: string;
}

export interface UseTeamMeetsOptions {
  /** F-202 — a coach with no athlete season still has a team to schedule
   *  meets for. When the caller is the single-team trainer, pass that team id
   *  so this hook (a) fetches the team's meets so the coach sees her own
   *  schedule, and (b) team-scopes the meets she creates. Falls back to the
   *  athlete's active-season team when absent. */
  coachTeamId?: string | null;
  /**
   * 2.0 wave 2 (W2-RACE-PAGE): skip the reads on mount. For a caller that only WRITES through this hook
   * (the "Add a race" sheet; the page that mounts it owns the list and reads it back itself), so opening
   * the sheet adds no `GET /api/meets`. Additive: absent or false, nothing changes. The writes below
   * still refetch this instance after they land, and `refetch()` still works when called.
   */
  skipInitialFetch?: boolean;
}

export function useTeamMeets(options: UseTeamMeetsOptions = {}): UseTeamMeetsResult {
  const { user, loading: authLoading } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const { data: season } = useActiveSeason();
  const userId = user?.id ?? null;
  // Athlete's active-season team wins; a coach-only account (no season) falls
  // back to the team she trains.
  const teamId = season?.team_id ?? options.coachTeamId ?? null;

  const [data, setData] = useState<TeamMeet[] | null>(null);
  const [loading, setLoading] = useState<boolean>(!options.skipInitialFetch);
  const [error, setError] = useState<Error | null>(null);
  /**
   * ⛔ B69 — the same missing idea as `useRaceResults`: which answer is
   * the current one. `createMeet` awaits a refetch while the mount
   * effect's refetch may still be out, and the older pair of GETs
   * landing last erases the meet that was just scheduled — it comes
   * back on the next mutation, which is exactly what "disappeared
   * after creating another entry" describes. Only the newest read
   * writes. (The solo+team merge already deduped by id.)
   */
  const seqRef = useRef(0);

  const fetchOne = useCallback(
    async (url: string): Promise<TeamMeet[]> => {
      const res = await fetchAuthed(url, { method: 'GET' });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`GET ${url} failed: ${res.status} ${text || res.statusText}`);
      }
      const body = (await res.json()) as ApiGetResponse;
      if (body.error) throw new Error(body.error);
      return Array.isArray(body.meets) ? body.meets : [];
    },
    [fetchAuthed],
  );

  const refetch = useCallback(async () => {
    if (authLoading) return;
    const seq = ++seqRef.current;
    if (!userId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Two parallel fetches: solo + team. Solo always fires;
      // team only when the athlete has an active-season team.
      const [solo, team] = await Promise.all([
        fetchOne('/api/meets'),
        teamId
          ? fetchOne(`/api/meets?teamId=${encodeURIComponent(teamId)}`)
          : Promise.resolve([] as TeamMeet[]),
      ]);
      // Merge + dedupe by id. Sort by meet_date ASC.
      const seen = new Set<string>();
      const merged: TeamMeet[] = [];
      for (const m of [...solo, ...team]) {
        if (seen.has(m.id)) continue;
        seen.add(m.id);
        merged.push(m);
      }
      merged.sort((a, b) => a.meet_date.localeCompare(b.meet_date));
      if (seq !== seqRef.current) return;
      setData(merged);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setData(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [authLoading, userId, teamId, fetchOne]);

  const skipInitialFetch = options.skipInitialFetch === true;
  useEffect(() => {
    if (skipInitialFetch) return;
    void refetch();
  }, [refetch, skipInitialFetch]);

  const createMeet = useCallback(
    async (input: CreateTeamMeetInput): Promise<TeamMeet> => {
      if (!userId) throw new Error('Not signed in');
      const res = await fetchAuthed('/api/meets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      const body = (await res.json().catch(() => ({}))) as ApiCreateResponse;
      if (!res.ok || body.error) {
        throw new Error(body.error || `POST /api/meets failed: ${res.status}`);
      }
      if (!body.meet) throw new Error('Server returned empty meet row');
      await refetch();
      return body.meet;
    },
    [userId, fetchAuthed, refetch],
  );

  const updateMeet = useCallback(
    async (id: string, patch: UpdateTeamMeetInput): Promise<TeamMeet> => {
      if (!userId) throw new Error('Not signed in');
      if (!id) throw new Error('Meet id required');
      const res = await fetchAuthed(`/api/meets/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const body = (await res.json().catch(() => ({}))) as ApiCreateResponse;
      if (!res.ok || body.error) {
        throw new Error(body.error || `PUT /api/meets/${id} failed: ${res.status}`);
      }
      if (!body.meet) throw new Error('Server returned empty meet row');
      await refetch();
      return body.meet;
    },
    [userId, fetchAuthed, refetch],
  );

  const deleteMeet = useCallback(
    async (id: string): Promise<void> => {
      if (!userId) throw new Error('Not signed in');
      if (!id) throw new Error('Meet id required');
      const res = await fetchAuthed(`/api/meets/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as ApiCreateResponse;
        throw new Error(body.error || `DELETE /api/meets/${id} failed: ${res.status}`);
      }
      await refetch();
    },
    [userId, fetchAuthed, refetch],
  );

  return { data, loading, error, refetch, createMeet, updateMeet, deleteMeet };
}
